import { FC, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { ThemeProvider } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import themeInarix from "../../../../../StylesMuiInarix";
import { selectLocale } from "../../../../../../redux/slices/layout";
import { State } from "../../../../../../redux/slices";
import {
  addRow,
  deleteRow,
  updateEmail,
  updateSite,
  updateRole,
  updateGroups,
  setReviewOpen,
  removeSuccessfulRows,
  setExistingEmails,
  GroupOption,
} from "../../../../../../redux/slices/users/managerCreation";
import createNewUserColumns from "./columns/columns";
import ReviewModal from "./ReviewModal/ReviewModal";
import { tableStyles as sx } from "./Table.styles";
import { createManagerUsers } from "../../../../../../redux/actions/users/managerAsyncActionsV2/managerCreationAsyncActionsV2";

const k = (key: string) => `Users_managers.table_v2.${key}`;

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    addRow: () => {
      dispatch(addRow());
    },
    deleteRow: (id: string) => {
      dispatch(deleteRow(id));
    },
    updateEmail: (id: string, value: string) => {
      dispatch(updateEmail({ id, value }));
    },
    updateSite: (
      id: string,
      siteId: string | null,
      siteName: string | null,
    ) => {
      dispatch(updateSite({ id, siteId, siteName }));
    },
    updateRole: (id: string, role: string | null, roleName: string | null) => {
      dispatch(updateRole({ id, role, roleName }));
    },
    updateGroups: (id: string, groups: GroupOption[]) => {
      dispatch(updateGroups({ id, groups }));
    },
    openReview: (open: boolean) => {
      dispatch(setReviewOpen(open));
    },
    sendInvitations: () => {
      dispatch(createManagerUsers());
    },
  };
};

const Table: FC = () => {
  const [t] = useTranslation("global");
  const locale = useSelector(selectLocale);
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const {
    addRow,
    deleteRow,
    updateEmail,
    updateSite,
    updateRole,
    updateGroups,
    openReview,
    sendInvitations,
  } = SetActions();
  const apiRef = useGridApiRef();

  const {
    rows,
    reviewOpen,
    roles,
    groups,
    locations,
    submitLoading,
    submitSuccess,
    submitResults,
  } = useSelector((state: State) => state.ManagerCreationReducer);

  const { users } = useSelector((state: State) => state.UsersReducer);

  const existingUsers = users.data.map((u) => u.username.toLowerCase());

  useEffect(() => {
    dispatch(setExistingEmails(existingUsers));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users.data]);

  const canReview =
    rows.length > 0 &&
    rows.every((r) => r.email.trim() !== "" && r.role !== null);

  const allSucceeded =
    submitSuccess &&
    submitResults.length > 0 &&
    submitResults.every((r) => r.success);

  // Redirect if submit finished successfully and the modal gets dismissed any other way
  // (browser back/forward, component remount within same session)
  useEffect(() => {
    if (allSucceeded && !reviewOpen) {
      dispatch(removeSuccessfulRows());
      navigate("/users");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSucceeded, reviewOpen]);

  const handleGoToRow = useCallback(
    (id: string) => {
      openReview(false);
      requestAnimationFrame(() => {
        const rowIndex = rows.findIndex((r) => r.id === id);
        if (rowIndex >= 0) {
          apiRef.current.scrollToIndexes({ rowIndex });
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, apiRef],
  );

  const columns = useMemo(
    () =>
      createNewUserColumns({
        t,
        sites: locations.map((loc) => ({ id: String(loc.id), name: loc.name })),
        roles,
        groups,
        onChangeEmail: (id, value) => updateEmail(id, value),
        onChangeSite: (id, val) =>
          updateSite(id, val?.id ?? null, val?.name ?? null),
        onChangeRole: (id, val) =>
          updateRole(id, val?.id ?? null, val?.name ?? null),
        onChangeGroups: (id, vals) => updateGroups(id, vals),
        onDeleteRow: (id) => deleteRow(id),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, roles, groups, locations],
  );

  console.log(rows);
  return (
    <ThemeProvider theme={themeInarix(locale)}>
      <Box sx={sx.wrapper}>
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={3}
          py={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" fontWeight={600}>
              {rows.length} {t(k("administrators"))}
            </Typography>
            <Typography variant="body2" sx={sx.requiredLabel}>
              {t(k("required_fields"))}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addRow()}
            sx={sx.addButton}
          >
            {t(k("add"))}
          </Button>
        </Box>

        {/* Grid */}
        <DataGridPro
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          getRowHeight={() => "auto"}
          getRowClassName={(params) =>
            params.row.status === "error" || params.row.status === "existing"
              ? "row-has-issue"
              : ""
          }
          disableRowSelectionOnClick
          hideFooter
          sx={sx.dataGrid}
        />

        {/* Footer */}
        <Box
          display="flex"
          justifyContent="flex-end"
          px={3}
          py={2}
          sx={sx.footer}
        >
          <Tooltip
            title={!canReview ? t(k("required_fields")) : ""}
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                disabled={!canReview}
                onClick={() => openReview(true)}
                sx={sx.reviewButton}
              >
                {t(k("review_button"))}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <ReviewModal
        open={reviewOpen}
        rows={rows}
        onClose={() => {
          if (allSucceeded) {
            dispatch(removeSuccessfulRows());
            navigate("/users");
          } else if (submitSuccess) {
            dispatch(removeSuccessfulRows());
            openReview(false);
          } else {
            openReview(false);
          }
        }}
        onGoToRow={handleGoToRow}
        onSend={sendInvitations}
        submitLoading={submitLoading}
        submitSuccess={submitSuccess}
        submitResults={submitResults}
        onChangeEmail={(rowId, value) => updateEmail(rowId, value)}
      />
    </ThemeProvider>
  );
};

export default Table;
